import { Box, CircularProgress, Pagination, Typography, MenuItem, Menu } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as S from './styles'
import { getOrders, OrderDetails } from 'src/common/api'
import FilterMenu from './FilterMenu'
import { useAbortController } from 'src/common/hooks'
import { alertNotification } from 'src/ui'
import { OrdersTable } from 'src/common/components'
import { logEvent } from '../../common/analytics/analytics'

export interface OrderFilter {
  type: {
    value: string
    label: string
  }
  items: {
    value: string
    label: string
  }[]
}

export const orderFilters: OrderFilter[] = [
  {
    type: {
      value: 'type',
      label: 'Type'
    },
    items: [
      {
        value: 'MINT',
        label: 'Mint'
      },
      {
        value: 'BURN',
        label: 'Burn'
      },
      {
        value: 'all',
        label: 'All'
      }
    ]
  },
  {
    type: {
      value: 'status',
      label: 'Status'
    },
    items: [
      {
        value: 'INITIATED',
        label: 'Initiated'
      },
      {
        value: 'PROCESSED',
        label: 'Processed'
      },
      {
        value: 'CANCELLED',
        label: 'Cancelled'
      },
      {
        value: 'QUEUED',
        label: 'Queued'
      },
      {
        value: 'EXPIRED',
        label: 'Expired'
      },
      {
        value: 'DECLINED',
        label: 'Declined'
      },
      {
        value: 'FAILED',
        label: 'Failed'
      },
      {
        value: 'all',
        label: 'All'
      }
    ]
  },
  {
    type: {
      value: 'chain',
      label: 'Chain'
    },
    items: [
      {
        value: 'ETHEREUM',
        label: 'Ethereum'
      },
      {
        value: 'BASE',
        label: 'Base'
      },
      {
        value: 'all',
        label: 'All'
      }
    ]
  },
  {
    type: {
      value: 'sortBy',
      label: 'Sort By: Newest'
    },
    items: [
      {
        value: 'NEWEST',
        label: 'Newest'
      },
      {
        value: 'OLDEST',
        label: 'Oldest'
      },
      {
        value: 'HIGHEST',
        label: 'Highest'
      },
      {
        value: 'LOWEST',
        label: 'Lowest'
      }
    ]
  }
]

export interface Query {
  type?: string
  status?: string
  chain?: string
  asset?: string
  fiatCurrency?: string
  sortBy: string
}

export interface SelectedFilters {
  type: string
  status: string
  chain: string
  asset: string
  fiatCurrency: string
  sortBy: string
}

const Orders: React.FC = () => {
  const { abort } = useAbortController()
  const [loading, setLoading] = useState(true)
  const [orders, setOrders] = useState<(OrderDetails | null)[]>([])
  const [query, setQuery] = useState<Query>({ sortBy: 'NEWEST' })
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    type: 'all',
    status: 'all',
    chain: 'all',
    asset: 'all',
    fiatCurrency: 'all',
    sortBy: 'NEWEST'
  })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    logEvent('button_click', { buttonName: 'New order' })
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const LIMIT = 10
  const [page, setPage] = React.useState({ current: 1, total: LIMIT, loading: true })

  const currentPageOffset = (page.current - 1) * LIMIT

  const fetchOrders = async (isQueryChanged = false) => {
    try {
      setLoading(true)
      const response = await getOrders({ ...query, page: isQueryChanged ? undefined : page.current }, abort())
      const { orders: fetchedOrders, numOfPages } = response
      if (!fetchedOrders.length) {
        setOrders([])
      } else if (isQueryChanged) {
        const newOrders = Array(numOfPages * LIMIT).fill(null)
        newOrders.splice(0, fetchedOrders.length, ...fetchedOrders)
        // add orders to the start of the array
        setOrders(newOrders)
        setPage({ current: 1, total: numOfPages, loading: false })
      } else {
        setOrders((prevOrders) => {
          const newOrders = prevOrders.slice()
          // add fetched orders to the orders array starting from the right page offset
          newOrders.splice(currentPageOffset, fetchedOrders.length, ...fetchedOrders)
          return newOrders
        })
      }
      setLoading(false)
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        alertNotification('Fetching orders history failed. Please contact support.', 'error')
      }
    }
  }

  useEffect(() => {
    setPage((prevPage) => ({ ...prevPage, loading: true }))
    fetchOrders(true)
  }, [query])

  useEffect(() => {
    if (page.current > 1 && !orders[currentPageOffset]) {
      fetchOrders()
    }
  }, [page.current])

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    if (page.current !== value) {
      setPage((prevPage) => ({ ...prevPage, current: value }))
    }
  }

  useEffect(() => {
    const start = performance.now()
    return () => {
      const duration = (performance.now() - start) / 1000
      logEvent('time_spent', { page: 'OrdersPage', duration_s: duration })
    }
  }, [])

  return (
    <S.StyledContainer maxWidth="xl" fixed>
      <S.HeaderContainer>
        <Typography variant="h5" fontWeight={600}>
          All Orders
        </Typography>
        <S.FiltersContainer>
          {orderFilters.map((filter) => (
            <FilterMenu
              key={filter.type.value}
              filter={filter}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              setQuery={setQuery}
            />
          ))}
          <S.NewOrderButton
            variant="contained"
            color="primary"
            onClick={handleClick}
            aria-controls={open ? 'new-order-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            + New Order
          </S.NewOrderButton>
          <Menu
            id="new-order-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <MenuItem component={Link} to="/mint" onClick={handleClose}>
              Mint Order
            </MenuItem>
            <MenuItem component={Link} to="/burn" onClick={handleClose}>
              Burn Order
            </MenuItem>
          </Menu>
        </S.FiltersContainer>
      </S.HeaderContainer>
      <S.StyledPaper variant="card">
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress disableShrink />
          </Box>
        ) : !orders.length ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Typography variant="h6">No Orders</Typography>
          </Box>
        ) : (
          <>
            <OrdersTable
              orders={
                orders.slice(currentPageOffset, currentPageOffset + LIMIT).filter((o) => o !== null) as OrderDetails[]
              }
              onRefresh={async () => await fetchOrders(true)}
            />
            {Boolean(orders.length) && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={page.total}
                  page={page.current}
                  onChange={handlePageChange}
                  color="primary"
                  disabled={page.loading}
                />
              </Box>
            )}
          </>
        )}
      </S.StyledPaper>
    </S.StyledContainer>
  )
}

export default Orders
